import { GripHorizontalIcon, HandIcon } from 'lucide-react'
import { Reorder, useDragControls } from 'motion/react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '~/ui'
import { cn } from '~/utils/classnames'

interface SelectionBarRootProps {
  children: ReactNode
  className?: string
}

function SelectionBarRoot({ children, className }: SelectionBarRootProps) {
  return (
    <div
      data-theme="protocol-dark"
      className={cn(
        'xs:bottom-2 xs:px-2 fixed bottom-0 left-0 z-20 w-full px-0',
        className
      )}
    >
      <div
        className={cn(
          'border-border/6 bg-background/95 xs:rounded-xl xs:border rounded-t-lg backdrop-blur-xl',
          'xs:flex-col flex flex-col-reverse'
        )}
      >
        {children}
      </div>
    </div>
  )
}

interface SelectionBarActionsProps {
  children: ReactNode
  className?: string
}

function SelectionBarActions({
  children,
  className,
}: SelectionBarActionsProps) {
  return (
    <div
      className={cn('flex items-center justify-between gap-1 p-2', className)}
    >
      {children}
    </div>
  )
}

function SelectionBarDragHint() {
  const { t } = useTranslation()

  return (
    <h3 className="text-foreground/64 xs:flex hidden items-center gap-1 pl-2 text-xs font-medium">
      <HandIcon />
      {t('action.drag_to_rearrange')}
    </h3>
  )
}

interface SelectionBarSaveButtonProps {
  onClick: () => void
  disabled?: boolean
  isLoading?: boolean
  children: ReactNode
  className?: string
}

function SelectionBarSaveButton({
  onClick,
  disabled,
  isLoading,
  children,
  className,
}: SelectionBarSaveButtonProps) {
  return (
    <Button
      onClick={onClick}
      isLoading={isLoading}
      disabled={disabled}
      className={cn(
        'max-xs:w-full',
        isLoading && 'pointer-events-none',
        className
      )}
    >
      {children}
    </Button>
  )
}

interface SelectionBarItemsProps {
  children: ReactNode
  className?: string
}

function SelectionBarItems({ children, className }: SelectionBarItemsProps) {
  return (
    <div className={cn('flex w-full overflow-auto p-2 pr-4', className)}>
      {children}
    </div>
  )
}

interface SelectionBarReorderGroupProps<T> {
  items: T[]
  getItemId: (item: T) => string
  onReorder: (newOrder: T[]) => void
  children: (item: T) => ReactNode
  className?: string
}

function SelectionBarReorderGroup<T>({
  items,
  getItemId,
  onReorder,
  children,
  className,
}: SelectionBarReorderGroupProps<T>) {
  if (items.length === 0) return null

  return (
    <Reorder.Group
      axis="x"
      values={items.map(getItemId)}
      onReorder={(newOrder) => {
        const reordered = newOrder.map(
          (id) => items.find((item) => getItemId(item) === id)!
        )
        onReorder(reordered)
      }}
      className={cn('mr-3 flex shrink-0 list-none gap-3', className)}
    >
      {items.map((item) => (
        <SelectionBarReorderItem key={getItemId(item)} value={getItemId(item)}>
          {children(item)}
        </SelectionBarReorderItem>
      ))}
    </Reorder.Group>
  )
}

function SelectionBarReorderItem({
  value,
  children,
}: {
  value: string
  children: ReactNode
}) {
  const dragControls = useDragControls()

  return (
    <Reorder.Item
      value={value}
      dragControls={dragControls}
      dragListener={false}
      whileDrag={{ zIndex: 50 }}
      className="relative isolate shrink-0 select-none"
    >
      {children}
      <div
        className="absolute inset-x-0 bottom-0 z-30 flex cursor-grab items-end justify-center rounded-b-md px-1.5 pb-1 active:cursor-grabbing"
        style={{ touchAction: 'none' }}
        onPointerDown={(e) => dragControls.start(e)}
      >
        <div className="bg-background/80 flex w-full rounded-full px-3 py-0.5 backdrop-blur-sm">
          <GripHorizontalIcon className="text-foreground/64 mx-auto size-3" />
        </div>
      </div>
    </Reorder.Item>
  )
}

interface SelectionBarPlaceholderProps {
  count?: number
  className?: string
}

function SelectionBarPlaceholder({
  count = 1,
  className,
}: SelectionBarPlaceholderProps) {
  if (count === 0) return null

  return (
    <div className="flex shrink-0 gap-3">
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className={cn(
            'border-border/8 bg-under/2 xs:h-24 xs:border-2 h-16 rounded-md border border-dashed',
            'aspect-square',
            className
          )}
        />
      ))}
    </div>
  )
}

const SelectionBar = Object.assign(SelectionBarRoot, {
  Actions: SelectionBarActions,
  DragHint: SelectionBarDragHint,
  SaveButton: SelectionBarSaveButton,
  Items: SelectionBarItems,
  ReorderGroup: SelectionBarReorderGroup,
  Placeholder: SelectionBarPlaceholder,
})

interface DrawerHeaderLayoutProps {
  children: ReactNode
  className?: string
}

function DrawerHeaderLayoutRoot({
  children,
  className,
}: DrawerHeaderLayoutProps) {
  return (
    <div
      className={cn(
        'border-border/4 relative flex items-center border-b p-2',
        className
      )}
    >
      {children}
    </div>
  )
}

interface DrawerHeaderContentProps {
  children: ReactNode
  className?: string
}

function DrawerHeaderLayoutContent({
  children,
  className,
}: DrawerHeaderContentProps) {
  return (
    <div className={cn('flex flex-1 items-center gap-4', className)}>
      {children}
    </div>
  )
}

const DrawerHeaderLayout = Object.assign(DrawerHeaderLayoutRoot, {
  Content: DrawerHeaderLayoutContent,
})

export { SelectionBar, DrawerHeaderLayout }
